import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { D2DTag, D2DNote } from '@/types/d2d';
import { getCompatibleTags } from '@/lib/d2dTagRules';
import { KTFollowUpDialog } from './KTFollowUpDialog';
import { AppointmentManagementDialog } from './AppointmentManagementDialog';
import { KTContactDialog } from './KTContactDialog';
import { WinPerformanceDialog } from './WinPerformanceDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface D2DTagSelectorProps {
  selectedTags: D2DTag[];
  onTagsChange: (tags: D2DTag[]) => void;
  onUpdateTagsOnly?: (unitId: string, tags: D2DTag[]) => Promise<void>;
  onKTFollowUp?: (date: Date, notes?: string, isManual?: boolean, customerEmail?: string) => void;
  onNAFollowUp?: (unitId: string, result: 'NA') => Promise<void>;
  unitId?: string;
  unitName?: string;
  className?: string;
  currentAppointment?: {
    date: Date;
    notes?: string;
  };
  onAppointmentChanged?: () => void;
  disableKTTag?: boolean;
  disabled?: boolean;
  unitNotes?: D2DNote[];
  currentEmail?: string;
  onAddNote?: (unitId: string, note: string) => Promise<void>;
  onUpdateEmail?: (unitId: string, email: string) => Promise<void>;
}

const TAG_CONFIG: Record<D2DTag, { label: string; variant: string; description: string; className?: string }> = {
  BK: { 
    label: '👤 BK', 
    variant: 'default', 
    description: 'Bestandskunde',
    className: 'bg-green-100 text-green-800 hover:bg-green-200'
  },
  NA: { 
    label: '❌ NA', 
    variant: 'secondary', 
    description: 'Nicht angetroffen',
    className: 'bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
  },
  HOT: { 
    label: '🔥 HOT', 
    variant: 'destructive', 
    description: 'Hot Lead' 
  },
  KT: { 
    label: '💬 KT', 
    variant: 'default', 
    description: 'In Kontakt',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
  },
  KI: { 
    label: '🚫 KI', 
    variant: 'outline', 
    description: 'Kein Interesse' 
  },
  NO: { 
    label: '⛔️ NO', 
    variant: 'outline', 
    description: 'Nicht optimierbar'
  },
  WIN: { 
    label: '🏆 WIN', 
    variant: 'default', 
    description: 'Gewonnener Lead',
    className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
  },
};

export const D2DTagSelector = ({ 
  selectedTags, 
  onTagsChange, 
  onUpdateTagsOnly,
  onKTFollowUp,
  onNAFollowUp,
  unitId,
  unitName,
  className,
  currentAppointment,
  onAppointmentChanged,
  disableKTTag = false,
  disabled = false,
  unitNotes = [],
  currentEmail = "",
  onAddNote,
  onUpdateEmail
}: D2DTagSelectorProps) => {
  const [showKTDialog, setShowKTDialog] = useState(false);
  const [showKTContactDialog, setShowKTContactDialog] = useState(false);
  const [showManagementDialog, setShowManagementDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showWinDialog, setShowWinDialog] = useState(false);
  const [pendingTag, setPendingTag] = useState<D2DTag | null>(null);
  const [pendingCustomerEmail, setPendingCustomerEmail] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingTag, setUpdatingTag] = useState<D2DTag | null>(null);

  /**
   * ========================================================================
   * CRITICAL: Database-first tag update
   * ========================================================================
   * This function is the SINGLE SOURCE OF TRUTH for tag updates.
   * It first updates the DB, then updates local state.
   * This prevents race conditions from parallel DB updates.
   * ========================================================================
   */
  const updateTagsToDatabase = async (newTags: D2DTag[], triggerNAFollowUp = false, clickedTag?: D2DTag) => {
    if (!unitId || !onUpdateTagsOnly) {
      // Fallback: just update local state if no DB function available
      onTagsChange(newTags);
      return;
    }

    // NO BLOCKING - allow parallel saves, just show visual feedback
    setIsUpdating(true);
    if (clickedTag) setUpdatingTag(clickedTag);
    console.log('💾 Database-first tag update:', { unitId, newTags });

    try {
      // STEP 1: Update database FIRST (this includes optimistic UI in useD2DData)
      await onUpdateTagsOnly(unitId, newTags);
      console.log('✅ Tags saved to database');

      // STEP 2: Trigger NA follow-up AFTER successful DB update
      if (triggerNAFollowUp && onNAFollowUp && newTags.includes('NA')) {
        console.log('🚀 Triggering smart NA scheduling');
        await onNAFollowUp(unitId, 'NA');
      }
    } catch (error) {
      console.error('❌ Tag update failed:', error);
      toast.error('Tag konnte nicht gespeichert werden', {
        description: 'Bitte erneut versuchen.',
      });
    } finally {
      setIsUpdating(false);
      setUpdatingTag(null);
    }
  };

  const handleTagClick = async (tag: D2DTag) => {
    // NO BLOCKING - allow clicks even while updating (parallel saves OK)

    console.log('🔥 D2DTagSelector: handleTagClick called', { tag, unitId, selectedTags });
    
    // ========================================================================
    // SPEZIAL-BEHANDLUNG: KT Tag (Kontakt/Termin)
    // Öffnet Dialog zur Terminerstellung
    // ========================================================================
    if (tag === 'KT') {
      if (selectedTags.includes('KT') && currentAppointment) {
        setShowManagementDialog(true);
      } else {
        const hasContactSent = unitNotes.some(note => 
          note.note.includes('Kontaktdaten an Kunden gesendet')
        );
        
        if (hasContactSent) {
          setShowKTDialog(true);
        } else {
          setShowKTContactDialog(true);
        }
      }
      return;
    }

    // ========================================================================
    // TAG-REGEL 1: BK Tag (Bestandskunde)
    // ========================================================================
    if (tag === 'BK') {
      console.log('✅ BK tag clicked - using centralized rules');
      const newTags = getCompatibleTags(selectedTags, 'BK');
      await updateTagsToDatabase(newTags, false, tag);
      return;
    }
    
    // ========================================================================
    // TAG-REGEL 2: HOT Tag (Hot Lead)
    // ========================================================================
    if (tag === 'HOT') {
      console.log('🔥 HOT tag clicked - using centralized rules');
      const newTags = getCompatibleTags(selectedTags, 'HOT');
      await updateTagsToDatabase(newTags, false, tag);
      return;
    }
    
    // ========================================================================
    // TAG-REGEL 3: NA Tag (Nicht Angetroffen)
    // ========================================================================
    if (tag === 'NA') {
      console.log('🎯 NA tag clicked', { alreadyActive: selectedTags.includes('NA') });
      
      // Auch wenn NA bereits aktiv ist, triggern wir ein neues Follow-up
      // um einen neuen Versuch/Impuls zu registrieren
      const newTags = getCompatibleTags(selectedTags, 'NA');
      
      // Wenn NA bereits aktiv ist, updaten wir last_contact_at und triggern neues Follow-up
      if (selectedTags.includes('NA')) {
        console.log('🔄 NA already active - triggering new follow-up attempt');
        // Trigger follow-up ohne Tag-Änderung (Tags bleiben gleich, aber neuer Versuch)
        if (onNAFollowUp && unitId) {
          setIsUpdating(true);
          setUpdatingTag('NA');
          try {
            await onNAFollowUp(unitId, 'NA');
          } finally {
            setIsUpdating(false);
            setUpdatingTag(null);
          }
        }
        return;
      }
      
      await updateTagsToDatabase(newTags, true, tag); // triggerNAFollowUp = true
      return;
    }
    
    // ========================================================================
    // TAG-REGEL 4: KI und NO Tags
    // ========================================================================
    if (tag === 'KI' || tag === 'NO') {
      console.log(`🔴 ${tag} tag clicked`);
      
      if (selectedTags.includes(tag)) {
        const newTags = selectedTags.filter(t => t !== tag);
        await updateTagsToDatabase(newTags, false, tag);
      } else {
        setPendingTag(tag);
        setShowConfirmDialog(true);
      }
      return;
    }
    
    // ========================================================================
    // TAG-REGEL 5: WIN Tag (Gewonnen)
    // ========================================================================
    if (tag === 'WIN') {
      console.log('🏆 WIN tag clicked');
      
      if (unitId) {
        setShowWinDialog(true);
      } else {
        const newTags = getCompatibleTags(selectedTags, 'WIN');
        await updateTagsToDatabase(newTags, false, tag);
      }
      return;
    }
    
    // Fallback for any other tags
    console.log(`⚠️ Unknown tag: ${tag} - toggling`);
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    await updateTagsToDatabase(newTags, false, tag);
  };

  /**
   * ========================================================================
   * KT FOLLOW-UP BESTÄTIGUNG
   * ========================================================================
   */
  const handleKTFollowUpConfirm = async (date: Date, notes?: string, customerEmail?: string) => {
    if (isUpdating) return;
    
    console.log('🎯 KT Follow-up confirmed', { currentTags: selectedTags });
    
    let newTags: D2DTag[] = selectedTags.filter(t => t !== 'NA' && t !== 'KI' && t !== 'NO');
    
    if (!newTags.includes('KT')) {
      newTags.push('KT');
    }
    
    if (!newTags.includes('HOT')) {
      newTags.push('HOT');
    }
    
    console.log('✅ New tags after KT confirmation:', newTags);
    
    // Database-first update
    await updateTagsToDatabase(newTags, false, 'KT');
    
    // Then trigger follow-up callback
    if (onKTFollowUp && date) {
      await onKTFollowUp(date, notes, true, customerEmail);
    }
    
    setShowKTDialog(false);
  };

  /**
   * ========================================================================
   * BESTÄTIGUNG FÜR KI/NO TAGS
   * ========================================================================
   */
  const handleConfirmTag = async () => {
    if (!pendingTag || isUpdating) {
      setShowConfirmDialog(false);
      return;
    }

    console.log(`✅ Confirming ${pendingTag} tag`);
    const newTags = getCompatibleTags(selectedTags, pendingTag);
    
    await updateTagsToDatabase(newTags, false, pendingTag);
    
    setPendingTag(null);
    setShowConfirmDialog(false);
  };

  const handleSendContact = async (email: string) => {
    if (!unitId || !onAddNote || !onUpdateEmail || !onUpdateTagsOnly || isUpdating) return;

    setIsUpdating(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { toast } = await import('sonner');
      
      const { data: unit } = await supabase
        .from('d2d_units')
        .select('customer_first_name')
        .eq('id', unitId)
        .single();

      const { error: emailError } = await supabase.functions.invoke('send-advisor-contact', {
        body: {
          customerEmail: email,
          customerFirstName: unit?.customer_first_name || '',
          unitId: unitId,
        },
      });

      if (emailError) {
        throw emailError;
      }

      await onUpdateEmail(unitId, email);
      await onAddNote(unitId, `Kontaktdaten an Kunden gesendet (${email})`);
      
      // Set KT and HOT tags - database-first
      const newTags = getCompatibleTags(selectedTags, 'KT');
      await onUpdateTagsOnly(unitId, newTags);

      setShowKTContactDialog(false);
      
      toast.success('Kontaktdaten erfolgreich gesendet', {
        description: `E-Mail wurde an ${email} gesendet`,
      });
    } catch (error) {
      console.error('Error sending contact:', error);
      const { toast } = await import('sonner');
      toast.error('Fehler beim Senden', {
        description: 'Die Kontaktdaten konnten nicht gesendet werden',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCreateFollowUpFromContact = async (email: string) => {
    if (!unitId) return;

    try {
      if (email && onUpdateEmail) {
        await onUpdateEmail(unitId, email);
      }
      
      setPendingCustomerEmail(email);
      setShowKTContactDialog(false);
      setShowKTDialog(true);
    } catch (error) {
      console.error('Error creating follow-up:', error);
    }
  };

  return (
    <>
      <div 
        className={cn("flex flex-wrap gap-1.5 sm:gap-2 relative z-40 w-full", className)}
        style={{ pointerEvents: 'auto' }}
      >
        {(Object.keys(TAG_CONFIG) as D2DTag[]).map((tag) => {
          const config = TAG_CONFIG[tag];
          const isSelected = selectedTags.includes(tag);
          const isKTTag = tag === 'KT';
          const isDisabled = (isKTTag && disableKTTag) || disabled;
          const isKITag = tag === 'KI';
          const isNOTag = tag === 'NO';
          const isThisTagUpdating = updatingTag === tag;
          
          return (
            <button
              key={tag}
              type="button"
              disabled={isDisabled}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 relative z-50 select-none",
                isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
                isThisTagUpdating && "animate-pulse",
                isKITag && isSelected && "border-transparent bg-red-600 text-white hover:bg-red-700 ring-4 ring-red-200 shadow-lg",
                isNOTag && isSelected && "border-transparent bg-orange-600 text-white hover:bg-orange-700 ring-4 ring-orange-200 shadow-lg",
                !isKITag && !isNOTag && isSelected && (
                  config.className || 
                  (config.variant === 'default' && "border-transparent bg-primary text-white hover:bg-primary/80") ||
                  (config.variant === 'secondary' && "border-transparent bg-accent text-white hover:bg-accent/80") ||
                  (config.variant === 'destructive' && "border-transparent bg-destructive text-white hover:bg-destructive/80") ||
                  (config.variant === 'outline' && "text-foreground border-border bg-background")
                ),
                !isSelected && "border-border text-foreground bg-background hover:bg-muted",
                !isKITag && !isNOTag && isSelected && "ring-2 ring-primary/20"
              )}
              onClick={(e) => {
                if (isDisabled) return;
                e.preventDefault();
                e.stopPropagation();
                console.log('🟢 Tag clicked:', tag, 'Current tags:', selectedTags);
                handleTagClick(tag);
              }}
              title={config.description}
              aria-pressed={isSelected}
            >
              {isThisTagUpdating && <Loader2 className="h-3 w-3 animate-spin" />}
              {config.label}
            </button>
          );
        })}
      </div>
      
      {showKTContactDialog && (
        <KTContactDialog
          open={showKTContactDialog}
          onOpenChange={setShowKTContactDialog}
          onSendContact={handleSendContact}
          onCreateFollowUp={handleCreateFollowUpFromContact}
          onAddNote={unitId && onAddNote ? (note) => onAddNote(unitId, note) : undefined}
          onActivateKTTag={unitId && onUpdateTagsOnly ? async () => {
            const newTags = getCompatibleTags(selectedTags, 'KT');
            await onUpdateTagsOnly(unitId, newTags);
          } : undefined}
          currentEmail={currentEmail}
          unitId={unitId}
          unitName={unitName}
          hasOfferSent={!!currentEmail}
        />
      )}

      {showKTDialog && (
        <KTFollowUpDialog
          open={showKTDialog}
          onOpenChange={(open) => {
            setShowKTDialog(open);
            if (!open) setPendingCustomerEmail('');
          }}
          onConfirm={handleKTFollowUpConfirm}
          unitId={unitId}
          unitTags={selectedTags}
          initialEmail={pendingCustomerEmail || currentEmail}
        />
      )}

      {showManagementDialog && currentAppointment && unitId && (
        <AppointmentManagementDialog
          open={showManagementDialog}
          onOpenChange={setShowManagementDialog}
          unitId={unitId}
          currentAppointment={currentAppointment}
          onAppointmentChanged={() => {
            setShowManagementDialog(false);
            onAppointmentChanged?.();
          }}
        />
      )}

      {showWinDialog && unitId && (
        <WinPerformanceDialog
          open={showWinDialog}
          onOpenChange={setShowWinDialog}
          unitId={unitId}
          onConfirm={async () => {
            // Tag setzen nach erfolgreicher Leistungserfassung
            const newTags = getCompatibleTags(selectedTags, 'WIN');
            await updateTagsToDatabase(newTags, false, 'WIN');
            
            // Termin entfernen bei WIN (KT wird durch getCompatibleTags bereits entfernt)
            if (selectedTags.includes('KT') && unitId) {
              try {
                const { supabase } = await import('@/integrations/supabase/client');
                await supabase
                  .from('d2d_units')
                  .update({ 
                    next_reminder: null, 
                    reminder_reason: null,
                    follow_up_notes: null,
                    manual_reminder: false,
                    appointment_token: null
                  })
                  .eq('id', unitId);
                console.log('✅ Termin bei WIN entfernt');
                onAppointmentChanged?.();
              } catch (error) {
                console.error('❌ Fehler beim Entfernen des Termins:', error);
              }
            }
          }}
        />
      )}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingTag === 'KI' ? 'Kein Interesse bestätigen' : 'Nicht optimierbar bestätigen'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTag === 'KI' 
                ? 'Möchten Sie diesen Lead wirklich als "Kein Interesse" markieren?'
                : 'Möchten Sie diesen Lead wirklich als "Nicht optimierbar" markieren?'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setPendingTag(null);
              setShowConfirmDialog(false);
            }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmTag} disabled={isUpdating}>
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
